import React, { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { FiRefreshCw, FiLogOut, FiMenu, FiWifi, FiWifiOff, FiMoon, FiSun } from 'react-icons/fi';
import { syncApi } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useOperations } from '../../contexts/OperationsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import NotificationCenter from '../common/NotificationCenter';

interface HeaderProps {
  onMenuToggle: () => void;
  signalRConnected?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, signalRConnected = true }) => {
  const { instance, accounts } = useMsal();
  const { hasWrite } = usePermissions();
  const { startOperation, updateProgress, completeOperation, failOperation } = useOperations();
  const { theme, toggleTheme } = useTheme();
  const { i18n } = useTranslation();

  const changeLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('app-language', lng);
  }, [i18n]);
  const [syncing, setSyncing] = useState(false);

  const account = accounts[0];
  const displayName = account?.name || account?.username || 'Uzytkownik';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    const opId = startOperation({
      type: 'sync',
      label: 'Synchronizacja z Amazon',
      progress: { current: 0, total: 4 },
    });

    try {
      updateProgress(opId, 1, 'Synchronizacja zamówień...');
      await syncApi.syncAll();
      updateProgress(opId, 2, 'Synchronizacja produktów...');

      // Simulated steps since syncAll is one call
      setTimeout(() => updateProgress(opId, 3, 'Synchronizacja stanów magazynowych...'), 1000);
      setTimeout(() => {
        updateProgress(opId, 4, 'Zakończono');
        completeOperation(opId, { success: 4, failed: 0, errors: [] });
      }, 2000);

      toast.success('Synchronizacja rozpoczęta');
    } catch (error: any) {
      failOperation(opId, error.response?.data?.message || 'Błąd synchronizacji');
      toast.error(error.response?.data?.message || 'Błąd synchronizacji');
    } finally {
      setSyncing(false);
    }
  }, [startOperation, updateProgress, completeOperation, failOperation]);

  const handleLogout = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  return (
    <header className="top-header">
      <div className="header-left">
        <button className="hamburger-btn" onClick={onMenuToggle} aria-label="Menu">
          <FiMenu size={20} />
        </button>
        <span className="header-title">Amazon Vendor & Seller Hub</span>
      </div>

      <div className="header-right">
        {/* Connection status */}
        <div
          className={`connection-indicator ${signalRConnected ? 'connected' : 'disconnected'}`}
          title={signalRConnected ? 'Połączono z serwerem' : 'Brak połączenia z serwerem'}
        >
          {signalRConnected ? <FiWifi size={12} /> : <FiWifiOff size={12} />}
        </div>

        {hasWrite('Sync' as any) && (
          <button
            className="header-sync-btn"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} size={14} />
            <span className="header-sync-label">
              {syncing ? 'Synchronizuję...' : 'Synchronizuj'}
            </span>
          </button>
        )}

        {/* Language selector */}
        <select
          value={i18n.language}
          onChange={(e) => changeLanguage(e.target.value)}
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            color: 'var(--text)',
            fontSize: '11px',
            padding: '2px 4px',
            cursor: 'pointer',
          }}
        >
          <option value="pl">🇵🇱 PL</option>
          <option value="en">🇬🇧 EN</option>
          <option value="de">🇩🇪 DE</option>
        </select>

        <button
          className="btn-icon"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Tryb jasny' : 'Tryb ciemny'}
          style={{ marginRight: '4px' }}
        >
          {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
        </button>

        <NotificationCenter />

        <div className="header-user">
          <div className="header-avatar">{initials}</div>
          <span className="header-user-name">{displayName}</span>
        </div>

        <button className="btn-icon" onClick={handleLogout} title="Wyloguj">
          <FiLogOut size={16} />
        </button>
      </div>
    </header>
  );
};

export default Header;
